import React from 'react';
import { Modal, Button, Tooltip } from 'antd';
import { ExclamationCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';

/**
 * DatasetExistsModal - A reusable modal component that notifies users when they try to upload
 * a dataset with a name that already exists.
 * 
 * Features:
 * - Warning icon and clear messaging
 * - Two action options: "Use existing" or "Rename this upload"
 * - Consistent styling with the application's design system
 * - Loading state support
 * 
 * @param {boolean} visible - Controls modal visibility
 * @param {function} onCancel - Called when user cancels/closes modal
 * @param {function} onUseExisting - Called when user chooses to use existing dataset
 * @param {function} onRenameUpload - Called when user chooses to rename and upload
 * @param {string} fileName - Name of the file being uploaded (without extension)
 * @param {boolean} loading - Shows loading state on buttons
 */

const DatasetExistsModal = ({
    visible,
    onCancel,
    onUseExisting,
    onRenameUpload,
    fileName,
    loading = false
}) => {
    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            footer={null}
            centered
            width={580}
            // Keep the overlay above the sticky header/nav (zIndex ~1200-1300)
            zIndex={2000}
            closable={true}
            maskClosable={false}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                paddingRight: '40px',
            }}>
                {/* Warning Icon */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '16px'
                }}>
                    <ExclamationCircleFilled
                        style={{
                            fontSize: '24px',
                            color: '#FAAD14',
                            marginRight: '12px'
                        }}
                    />
                    <div style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#262626',
                        lineHeight: '1.4'
                    }}>
                        A dataset with this name already exists
                    </div>
                </div>

                {/* Message */}
                <div style={{
                    fontSize: '14px',
                    color: '#595959',
                    lineHeight: '1.5',
                    marginBottom: '24px',
                    marginLeft: '36px'
                }}>
                    You've already uploaded a test set named "{fileName}". What would you like to do?
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    width: '100%',
                    marginLeft: '36px'
                }}>
                    <Button
                        onClick={onUseExisting}
                        disabled={loading}
                        style={{
                            height: '32px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            border: '1px solid #d9d9d9',
                            backgroundColor: '#ffffff',
                            color: '#262626',
                            padding: '0 16px',
                        }}
                    >
                        <span>Use existing</span>
                        <Tooltip title="It will add the new examples to the existing dataset">
                            <QuestionCircleOutlined style={{ color: '#878787' }} />
                        </Tooltip>

                    </Button>
                    <Button
                        type="primary"
                        onClick={onRenameUpload}
                        loading={loading}
                        disabled={loading}
                        style={{
                            height: '32px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: '#1777ff',
                            borderColor: '#1777ff',
                            padding: '0 16px',
                        }}
                    >
                        Rename this upload
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default DatasetExistsModal;
